/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  CacheEvictor,
  ChallengerApi,
  ChallengerCacheEviction,
  ChallengerHttpClient,
  LibtoolVersion,
  ObservabilityEvent,
  ObservableHttpClientLibrary,
  TalerError,
  TalerErrorCode,
} from "@gnu-taler/taler-util";
import {
  ComponentChildren,
  Fragment,
  FunctionComponent,
  VNode,
  createContext,
  h,
} from "preact";
import { useContext, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { ErrorLoading } from "../components/ErrorLoading.js";
import { Button } from "../components/Button.js";
import { BrowserFetchHttpLib } from "../utils/http-impl.sw.js";
import {
  APIClient,
  ActiviyTracker,
  ChallengerLib,
  Subscriber,
} from "./activity.js";
import { useTranslationContext } from "./translation.js";

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

export type ChallengerContextType = {
  url: URL;
  config: ChallengerApi.ChallengerTermsOfServiceResponse;
  lib: ChallengerLib;
  hints: VersionHint[];
  onActivity: Subscriber<ObservabilityEvent>;
  cancelRequest: (eventId: string) => void;
};

// @ts-expect-error default value to undefined, should it be another thing?
const ChallengerContext = createContext<ChallengerContextType>(undefined);

export const useChallengerApiContext = (): ChallengerContextType =>
  useContext(ChallengerContext);

enum VersionHint {
  NONE,
}

type Evictors = {
  challenger?: CacheEvictor<ChallengerCacheEviction>;
};
const NO_EVICTORS: Evictors = {};

type ConfigResult<T> =
  | undefined
  | { type: "ok"; config: T; hints: VersionHint[] }
  | { type: "incompatible"; serverVersion?: string; supported: string }
  | { type: "error"; error: TalerError };

export type ChallengerApiProviderStatus =
  | { type: "loading"; attempt: number; maxAttempts: number }
  | { type: "error"; error: TalerError; retry: () => void }
  | { type: "incompatible"; serverVersion?: string; supported: string };

const CONFIG_RETRY_DELAYS_MS = [1000, 3000] as const;
const CONFIG_AUTO_RETRY_COUNT = 2;

export const ChallengerApiProvider = ({
  baseUrl,
  children,
  frameOnError,
  renderStatus,
  evictors = NO_EVICTORS,
}: {
  baseUrl: URL;
  children: ComponentChildren;
  evictors?: Evictors;
  frameOnError: FunctionComponent<{ children: ComponentChildren }>;
  renderStatus?: (status: ChallengerApiProviderStatus) => ComponentChildren;
}): VNode => {
  const [checked, setChecked] =
    useState<ConfigResult<ChallengerApi.ChallengerTermsOfServiceResponse>>();
  const [retryRequest, setRetryRequest] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const checkedFor = useRef<string>();
  const endpointKey = baseUrl.href;
  const { i18n } = useTranslationContext();

  const { getRemoteConfig, VERSION, lib, cancelRequest, onActivity } = useMemo(
    () => buildChallengerApiClient(baseUrl, evictors),
    [baseUrl, evictors],
  );

  useEffect(() => {
    let active = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    setChecked(undefined);
    setAttempt(0);
    checkedFor.current = undefined;
    async function testConfig(): Promise<void> {
      try {
        const config = await getRemoteConfig();
        if (!active) return;
        checkedFor.current = endpointKey;
        if (LibtoolVersion.isCompatible(VERSION, config.version)) {
          setAttempt(0);
          setChecked({ type: "ok", config, hints: [] });
        } else {
          setChecked({
            type: "incompatible",
            serverVersion: config.version,
            supported: VERSION,
          });
        }
      } catch (error) {
        if (!active) return;
        checkedFor.current = endpointKey;
        if (error instanceof TalerError) {
          if (
            error.hasErrorCode(
              TalerErrorCode.GENERIC_CLIENT_UNSUPPORTED_PROTOCOL_VERSION,
            )
          ) {
            setChecked({
              type: "incompatible",
              supported: VERSION,
              serverVersion: extractServerVersion(error),
            });
            return;
          }
          setChecked({ type: "error", error });
        } else {
          setChecked({ type: "error", error: TalerError.fromException(error) });
        }
        const currentError =
          error instanceof TalerError ? error : TalerError.fromException(error);
        if (
          attempts < CONFIG_AUTO_RETRY_COUNT &&
          isRetriableConfigError(currentError)
        ) {
          attempts++;
          setAttempt(attempts);
          setChecked(undefined);
          retryTimer = setTimeout(
            testConfig,
            CONFIG_RETRY_DELAYS_MS[attempts - 1],
          );
        }
      }
    }
    testConfig();
    return () => {
      // on unload, stop retry
      active = false;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
    };
  }, [getRemoteConfig, VERSION, endpointKey, retryRequest]);

  const currentChecked =
    checkedFor.current === endpointKey ? checked : undefined;

  if (currentChecked === undefined) {
    return h(frameOnError, {
      children: renderStatus
        ? renderStatus({
            type: "loading",
            attempt,
            maxAttempts: CONFIG_AUTO_RETRY_COUNT,
          })
        : h(
            "div",
            {},
            i18n.str`Checking compatibility of this webapp with backend service...`,
          ),
    });
  }
  if (currentChecked.type === "error") {
    const retry = () => setRetryRequest((current) => current + 1);
    return h(frameOnError, {
      children: renderStatus
        ? renderStatus({ type: "error", error: currentChecked.error, retry })
        : h(Fragment, null, [
            h(ErrorLoading, {
              title: i18n.str`There was an error trying to contact the backend service.`,
              error: currentChecked.error,
            }),
            h(
              Button,
              {
                class: "button is-info mt-2",
                onClick: retry,
              },
              i18n.str`Retry`,
            ),
          ]),
    });
  }
  if (currentChecked.type === "incompatible") {
    return h(frameOnError, {
      children: renderStatus
        ? renderStatus(currentChecked)
        : h(
            "div",
            {},
            currentChecked.serverVersion
              ? i18n.str`The server version is not supported. Supported version "${currentChecked.supported}", server version "${currentChecked.serverVersion}"`
              : i18n.str`The server version is not supported. Supported version "${currentChecked.supported}".`,
          ),
    });
  }

  const value: ChallengerContextType = {
    url: baseUrl,
    config: currentChecked.config,
    onActivity: onActivity,
    lib,
    cancelRequest,
    hints: currentChecked.hints,
  };
  return h(ChallengerContext.Provider, {
    value,
    children,
  });
};

function isRetriableConfigError(error: TalerError): boolean {
  const status = (
    error.errorDetail as TalerError["errorDetail"] & {
      httpStatusCode?: unknown;
    }
  ).httpStatusCode;
  return (
    status === undefined ||
    (typeof status === "number" && status >= 500 && status <= 599)
  );
}

function extractServerVersion(error: TalerError): string | undefined {
  const detail = error.errorDetail.detail;
  if (typeof detail !== "string") return undefined;
  return /server supports ([^,\s]+)/.exec(detail)?.[1];
}

function buildChallengerApiClient(
  url: URL,
  evictors: Evictors,
): APIClient<ChallengerLib, ChallengerApi.ChallengerTermsOfServiceResponse> {
  const httpFetch = new BrowserFetchHttpLib({
    enableThrottling: true,
    requireTls: false,
  });
  const tracker = new ActiviyTracker<ObservabilityEvent>();
  const httpLib = new ObservableHttpClientLibrary(httpFetch, {
    observe(ev) {
      tracker.notify(ev);
    },
  });

  const challenger = new ChallengerHttpClient(
    url.href,
    httpLib,
    evictors.challenger,
  );

  async function getRemoteConfig(): Promise<ChallengerApi.ChallengerTermsOfServiceResponse> {
    const resp = await challenger.getConfig();
    if (resp.type === "fail") {
      if (resp.detail) {
        throw TalerError.fromUncheckedDetail({
          ...resp.detail,
          httpStatusCode: resp.case,
        });
      } else {
        throw TalerError.fromException(
          new Error("failed to get challenger remote config"),
        );
      }
    }
    return resp.body;
  }

  return {
    getRemoteConfig,
    VERSION: ChallengerHttpClient.PROTOCOL_VERSION,
    lib: {
      challenger,
    },
    onActivity: tracker.subscribe,
    cancelRequest: (id) => httpLib.cancelRequest(id),
  };
}
